/**
 * Feature Flag Hook
 *
 * Reusable hook for checking feature flag status via environment variables.
 * DRY: Single utility for all current and future feature flags.
 *
 * Feature flags follow the naming convention: NEXT_PUBLIC_FEATURE_{FLAG_NAME}
 * Default behavior: feature is enabled unless explicitly set to 'false'.
 *
 * IMPORTANT: Next.js replaces process.env.* at BUILD TIME via static analysis.
 * Dynamic access like process.env[dynamicKey] CANNOT be replaced and will
 * always evaluate to undefined in production. Always use explicit property
 * access for each known flag.
 *
 * @module useFeatureFlag
 */

/**
 * Check if a feature flag is enabled.
 *
 * @param flag - Feature flag name (e.g., 'AI_CHATBOT', 'DARK_MODE')
 * @returns true if the feature is enabled, false otherwise
 *
 * @example
 * const isEnabled = useFeatureFlag('AI_CHATBOT');
 * if (!isEnabled) return null;
 */
export function useFeatureFlag(flag: string): boolean {
  // Rule 15: Explicit property access is required for Next.js build-time
  // env replacement. Dynamic access (process.env[key]) evaluates to
  // undefined because Next.js cannot statically analyze it.
  switch (flag) {
    case 'AI_CHATBOT':
      return process.env.NEXT_PUBLIC_FEATURE_AI_CHATBOT !== 'false';
    case 'DEMO_MODE':
      return process.env.NEXT_PUBLIC_FEATURE_DEMO_MODE !== 'false';
    case 'DARK_MODE':
      return process.env.NEXT_PUBLIC_FEATURE_DARK_MODE !== 'false';
    default:
      // Unknown flags default to enabled (fail-open for safety)
      return true;
  }
}
